import { gql } from '@apollo/client';
import * as MENUS from '../constants/menus';
import { BlogInfoFragment } from '../fragments/GeneralSettings';
import { MediaBoosterSettingsFragment } from '../fragments/MediaBoosterSettings';
import { UserProfileFragment } from '../fragments/UserProfile';
import { PageSEOFragment } from '../fragments/pages/PageSEOFragment';
import { OptimizedImageFragment } from '../fragments/artikler/ArtikkelSEOFragment';
import { ContactCTAAlternative } from '../components'
import {
  Header,
  Footer,
  Main,
  Container,
  NavigationMenu,
  SEO,
  SchemaMarkup,
} from '../components';
import KontaktHero from '../components/ContactPage/KontaktHero/KontaktHero';
import EmployeeGrid from '../components/ContactPage/EmployeeGrid/EmployeeGrid';
import GravityForm from '../components/ContactPage/GravityForm/GravityForm';
import React, { useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import TopHero from '../components/skeletonloader/TopHero';
import { createSchema, createBreadcrumbSchema } from '../lib/schema';
import { mapEmployeesToPersonSchemas } from '../lib/employee-schema';
import { getDispatchedPageSeo } from '../lib/page-seo-helpers';
import { POLYLANG_URI_FIELDS } from '../lib/graphql-polylang-fields';
import { fetchHeaderFooterSharedData } from '../lib/fetch-header-footer-shared-data';
import { ANSATT_LANDING_PRESENCE_FIELDS } from '../fragments/ansatt/AnsattContentFragment';

export default function Kontakt(props) {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Use data from getStaticProps
  const { data, loading = false, error = null } = { data: props.data || {} };

  useEffect(() => {
    setMounted(true);
    
    // Check if mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (error) return <div>Error loading page</div>;

  const { title: siteTitle, description: siteDescription } = data?.generalSettings ?? {};
  const primaryMenu = data?.headerMenuItems?.nodes ?? [];
  const footerMenu = data?.footerMenuItems?.nodes ?? [];
  const lightLogo = data?.mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const viewer = data?.viewer;
  const page = data?.page || data?.nodeByUri;
  const pageSeo = getDispatchedPageSeo(router.asPath, page, 'kontakt');

  // Find the hero section from the flexible content
  const heroSection = page?.kontaktoss?.kontaktSide?.find(
    section => section.fieldGroupName === 'KontaktossKontaktSideHeroLayout'
  );

  // Get the page description from the hero section or fall back to site description
  const pageDescription = heroSection?.subText || siteDescription;
  
  // SEO values with comprehensive social media metadata
  const pageTitle = page?.seo?.title || 'Kontakt';
  const siteName = siteTitle || 'Mediabooster';
  const finalTitle = pageTitle?.includes(siteName) ? pageTitle : `${pageTitle} - ${siteName}`;
  const defaultDescription =
    pageSeo.code === 'en'
      ? 'Contact Mediabooster for digital marketing, web design, and technology. We help your business grow online.'
      : 'Ta kontakt med Mediabooster for digital markedsføring, webdesign og teknologi. Vi hjelper din bedrift med å vokse digitalt.';
  const finalDescription = page?.seo?.description || pageDescription || defaultDescription;
  
  // Get social image from SEO settings or use fallback
  const socialImage = page?.seo?.openGraph?.image?.url || page?.seo?.openGraph?.image?.secureUrl || 'https://mediaboosterhe.wpenginepowered.com/wp-content/uploads/2025/01/tjenester-mediabooster.png';
  const socialImageAlt = page?.seo?.openGraph?.image?.altText || 'Mediabooster - Kontakt oss for digital markedsføring og webdesign';

  // Modified hero section data to use our form toggle
  const modifiedHeroSection = heroSection ? {
    ...heroSection,
    contactBoxes: {
      ...heroSection.contactBoxes,
      contactForm: null // Set to null to prevent gravity form from showing
    }
  } : null;

  const isLoading = loading || !mounted;
  const languageCode = pageSeo.code === 'en' ? 'en' : 'no';
  const employees = data?.employees?.nodes || [];

  const contactSchema = useMemo(
    () =>
      createSchema([
        createBreadcrumbSchema(pageSeo.breadcrumbs),
        ...mapEmployeesToPersonSchemas(employees, languageCode),
      ]),
    [employees, languageCode, pageSeo]
  );

  return (
    <>
      <SEO
        locale={pageSeo.locale}
        hreflangAlternates={pageSeo.hreflangAlternates}
        seo={{
          title: finalTitle,
          description: finalDescription,
          canonicalUrl: page?.seo?.canonicalUrl || pageSeo.canonicalUrl,
          robots: page?.seo?.robots,
          openGraph: {
            title: finalTitle,
            description: finalDescription,
            url: page?.seo?.canonicalUrl || pageSeo.canonicalUrl,
            siteName: siteTitle || 'Mediabooster',
            type: 'website',
            image: {
              url: socialImage,
              secureUrl: socialImage,
              width: 1200,
              height: 630,
              alt: socialImageAlt,
            },
          },
          twitter: {
            card: 'summary_large_image',
            site: '@mediabooster',
            title: finalTitle,
            description: finalDescription,
            image: socialImage,
          },
        }}
      />
      <SchemaMarkup schema={contactSchema} />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
        viewer={viewer}
        sharedData={props.sharedData}
        languageCode={pageSeo.code}
      />
      <Main>
        {isLoading ? (
          <TopHero />
        ) : (
          <>
            <KontaktHero 
              data={modifiedHeroSection} 
              onFormToggle={() => setShowForm(!showForm)} 
              showForm={showForm}
            />
            <EmployeeGrid data={data?.employees} />
            <div style={{ padding: isMobile ? '2rem 1rem' : '0 0 0rem 0' }}>
              <ContactCTAAlternative sharedData={props.sharedData} />
            </div>
          </>
        )}
      </Main>
      <Footer
        title={siteTitle}
        menuItems={primaryMenu}
        sharedData={props.sharedData}
        languageCode={pageSeo.code}
      />
    </>
  );
}

Kontakt.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${UserProfileFragment}
  ${PageSEOFragment}
  ${OptimizedImageFragment}
  query GetKontaktPage(
    $headerLocation: MenuLocationEnum
    $footerLocation: MenuLocationEnum
  ) {
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    ...UserProfileFragment
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    page(id: "kontakt", idType: URI) {
      ...PageSEOFragment
      ${POLYLANG_URI_FIELDS}
      kontaktoss {
        kontaktSide {
          ... on KontaktossKontaktSideHeroLayout {
            fieldGroupName
            mainHeaderText
            smallTopHeader
            subText
            contactBoxes {
              contactForm
              email
              phoneNumber
            }
          }
        }
      }
    }
    employees: ansatte(first: 100, where: { language: NB }) {
      nodes {
        title
        slug
        id
        ansattId
        ansatte {
          employeePhoto {
            node {
              ...OptimizedImageFragment
            }
          }
          role
          rekkefolge
          email
          linkedin
        }
        ${ANSATT_LANDING_PRESENCE_FIELDS}
      }
    }
  }
`;

Kontakt.variables = () => {
  return {
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
  };
};

// Add getStaticProps for server-side data fetching with shared data
export async function getStaticProps() {
  const { getApolloClient } = await import('@faustwp/core');
  const { MeetingBookingCtaAlternativeFragment } = await import('../fragments/MeetingBookingCta');
  const apolloClient = getApolloClient();
  
  try {
    // Fetch page-specific data
    const { data: pageData } = await apolloClient.query({
      query: Kontakt.query,
      variables: Kontakt.variables(),
      fetchPolicy: 'no-cache'
    });

    let sharedData = {};
    try {
      sharedData = await fetchHeaderFooterSharedData(apolloClient, 'no', {
        extraFragments: [MeetingBookingCtaAlternativeFragment],
        mediaboosterSettingsSpread: '...MeetingBookingCtaAlternativeFragment',
      });
    } catch (sharedError) {
      console.error('Error fetching kontakt shared data:', sharedError);
    }

    return {
      props: {
        data: pageData,
        sharedData,
      },
      revalidate: 60 * 15, // Revalidate every 15 minutes
    };
  } catch (error) {
    console.error('Error fetching kontakt data:', error);
    return {
      props: {
        data: {},
        sharedData: {}
      },
      revalidate: 60 * 5, // Retry more frequently on error
    };
  }
}



